// Reordering trace events to explore a different interleaving.
//
// The trace view lets a user drag an event past another; that is a request to
// overrule the scheduler's tie-break, which means re-running the program from
// the beginning. This module is the pure half of that: grouping rows into
// scheduler steps, deciding which drags are legal, turning a drag into a
// `ScheduleOverride`, and replaying a stored list of reorderings against fresh
// source. Nothing here touches the DOM. See docs/design/trace-reordering.md.
//
// The unit that moves is the **step**, not the row: a step is one `gen.next()`
// on one thread — everything it does between two consecutive pauses — so the
// events it emits have no interleaving point between them and no schedule could
// separate them. Some steps emit no events at all (`docs/concurrency.md` calls
// them *empty steps*, and explains why they are ordinary): they have no rows and
// are invisible here, but they still occupy a position, so step indices and
// group indices are not the same thing — and an empty step travels with the
// visible one below it (see `stepSpan`), or every drag would be refused for a
// reason that isn't on screen.

import { run } from "../src/compiler.js";
import type { RunOptions, RunResult } from "../src/compiler.js";
import type { Causality, StepId } from "../src/backend/interp/causality.js";

/** A user's stored reordering, in names that survive a re-run. */
export interface Reorder {
  /** The step that was picked up. */
  moved: StepId;
  /** The step it was dropped onto. */
  target: StepId;
  /** Which side of `target` `moved` should end up on. */
  side: "before" | "after";
}

export type ReorderStatus =
  /** Realized: both steps were found and the new trace has them in this order. */
  | "applied"
  /** A step id is gone — an earlier reordering changed that thread's history. */
  | "stale"
  /** The steps were found but the forced window couldn't be honored. */
  | "unrealized";

/** The contiguous rows one scheduler step emitted. Steps that emitted none are
 *  absent (they have nothing to drag), so `step` is not the array index. */
export interface StepGroup {
  step: number;
  thread: number;
  time: number;
  /** Trace rows this step emitted, as `[firstRow, endRow)`. */
  firstRow: number;
  endRow: number;
}

/** Visible step groups, in trace order. */
export function stepGroups(c: Causality): StepGroup[] {
  const out: StepGroup[] = [];
  c.steps.forEach((s, step) => {
    if (s.endEvent > s.firstEvent) {
      out.push({ step, thread: s.thread, time: s.time, firstRow: s.firstEvent, endRow: s.endEvent });
    }
  });
  return out;
}

/** The step index a trace row belongs to (-1 if the row is out of range). */
export function stepOfRow(c: Causality, row: number): number {
  return c.eventStep[row] ?? -1;
}

/**
 * The steps that travel with `step` when it is dragged `dir` — the step itself
 * plus the run of empty steps of its own thread on the side it is heading, each
 * chained to the next by `enabledBy`.
 *
 * An empty step has no row: the user can't see it and can't grab it. But it
 * brackets the visible one causally, so leaving it behind refuses the drag for
 * an invisible reason. They are everywhere — every memory access is an
 * interleaving point, so a thread pauses on either side of a load — and *which*
 * of them must travel depends on the direction, because rule 2 binds a different
 * end each way:
 *
 *  - **Dragging up**, the empty steps *above* travel. They are what made this
 *    step runnable, so rule 2 blocks the drag against a step that isn't on
 *    screen. The ones below stay: nothing requires them to move, and dragging
 *    them along would force the thread to run twice in a row and quietly change
 *    the interleaving the user asked for.
 *  - **Dragging down**, the empty steps *below* travel, by the mirror of rule 2:
 *    this step is what made them runnable, so it cannot sink past them. Since a
 *    thread pauses immediately after doing anything, that pause sits directly
 *    below every single-row step — which froze all of them. In
 *    `lost_update.stm` no read could be dragged down at all, and the demo had to
 *    be performed by dragging another thread's row *up* past it instead.
 *
 * *Adjacent in the trace* is the condition that is easy to lose. An empty step
 * is only in the way when nothing ran between it and the step it is chained to.
 * Once another thread has run in between, it is ordinary history — the drag
 * can't cross it (rule 2 still holds) but it doesn't travel either. Following
 * `enabledBy` without the adjacency check swallows every intervening step into
 * the span, and since the span is used as a contiguous index range, every drop
 * inside it is then refused as "nothing to move".
 */
export function stepSpan(
  c: Causality,
  step: number,
  dir: "up" | "down"
): { first: number; last: number } {
  const thread = c.steps[step]?.thread;
  const empty = (i: number): boolean => {
    const s = c.steps[i];
    return !!s && s.thread === thread && s.endEvent === s.firstEvent;
  };
  let first = step;
  let last = step;
  if (dir === "up") while (empty(first - 1) && c.steps[first]!.enabledBy === first - 1) first--;
  else while (empty(last + 1) && c.steps[last + 1]!.enabledBy === last) last++;
  return { first, last };
}

/**
 * Whether `moved` may be dropped at `target`'s position.
 *
 * Two conditions, and they are the whole rule (see the design note):
 *
 *  1. **Same virtual instant** across the window. Virtual time is the model of
 *     time, not a scheduling choice; crossing it would run a thread in the past.
 *  2. **Never above the step that enabled it.** Every step was made runnable by
 *     some earlier step's `enqueue` (`StepRecord.enabledBy`), which covers a
 *     thread against itself, a child against its `spawn`, an acquire against the
 *     release that freed the lock, and a `retry` re-attempt against the commit
 *     that woke it. Downward, the mirror: a step can't sink below one it enabled.
 *
 * Note what is *not* here: reads-from. That a read observed a particular write
 * does not stop it from moving above that write — it would simply see the older
 * value, which is a perfectly realizable schedule and the entire point of the
 * feature. Data-flow edges shape the event's *id* (so the change is visible),
 * not its mobility.
 */
export function canDrop(c: Causality, moved: number, target: number): boolean {
  return blocker(c, moved, target) === null;
}

/** Why `moved` can't be dropped at `target`, or null when it can. Phrased for
 *  the drag UI, which shows it instead of just refusing. */
export function blocker(c: Causality, moved: number, target: number): string | null {
  const steps = c.steps;
  const from = steps[moved];
  const to = steps[target];
  if (!from || !to) return "nothing to move";
  const span = stepSpan(c, moved, target < moved ? "up" : "down");
  if (target >= span.first && target <= span.last) return "nothing to move";

  const lo = Math.min(span.first, target);
  const hi = Math.max(span.last, target);
  for (let k = lo; k <= hi; k++) {
    if (steps[k]!.time !== from.time) {
      return `crosses t=${steps[k]!.time.toFixed(2)} — a thread can't run in the past`;
    }
  }

  if (target < span.first) {
    if (steps[span.first]!.enabledBy >= target) {
      return `T${from.thread} only became runnable after step ${steps[span.first]!.enabledBy}`;
    }
    return null;
  }
  for (let k = span.last + 1; k <= target; k++) {
    const by = steps[k]!.enabledBy;
    if (by >= span.first && by <= span.last) {
      return `T${steps[k]!.thread} only became runnable because of this step`;
    }
  }
  return null;
}

/** The step positions `moved` may legally be dropped at, as an inclusive range
 *  of step indices. Both ends are clamped to `moved` when it can't move. */
export function dropRange(c: Causality, moved: number): { lo: number; hi: number } {
  let lo = stepSpan(c, moved, "up").first;
  let hi = stepSpan(c, moved, "down").last;
  while (lo > 0 && canDrop(c, moved, lo - 1)) lo--;
  while (hi < c.steps.length - 1 && canDrop(c, moved, hi + 1)) hi++;
  return { lo, hi };
}

/**
 * The scheduler override that realizes the drag: the window of steps between
 * the two positions, with `moved` lifted out and re-inserted at `target`.
 *
 * Steps before the window need no forcing — a run is deterministic for a given
 * seed, so replaying the prefix under the ordinary heuristic reproduces it
 * exactly. Steps after it need none either, for entries already queued: a ready
 * entry's tie-break priority is a hash of `(seed, enqueue ordinal)` fixed when
 * it was queued, so overruling one decision leaves every pending entry's
 * priority — and therefore their relative order — untouched. Entries created
 * *after* the divergence are new work and order themselves freshly, which is
 * the part of the trace the reordering was meant to change.
 */
export function toOverride(
  c: Causality,
  moved: number,
  target: number
): { from: number; threads: number[] } {
  const span = stepSpan(c, moved, target < moved ? "up" : "down");
  const lo = Math.min(span.first, target);
  const hi = Math.max(span.last, target);
  const window = c.steps.slice(lo, hi + 1).map((s) => s.thread);
  const lifted = window.splice(span.first - lo, span.last - span.first + 1);
  // Upward: the span lands on the target's index. Downward: everything after
  // the lift has shifted left by the span's length, so the target now sits at
  // `target - lo - lifted.length` and the span goes just after it.
  const at = target < span.first ? target - lo : target - lo - lifted.length + 1;
  window.splice(at, 0, ...lifted);
  return { from: lo, threads: window };
}

/** The stored form of a drag, in ids rather than positions. */
export function toReorder(c: Causality, moved: number, target: number): Reorder {
  return {
    moved: c.steps[moved]!.id,
    target: c.steps[target]!.id,
    side: target < moved ? "before" : "after",
  };
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

export interface ReorderedRun {
  /** The last successful run — the one the trace view should show. */
  result: RunResult;
  /** One status per entry of the input list, index-aligned. */
  statuses: ReorderStatus[];
  /** Set when a re-run threw (a reordering can deadlock a program, or push it
   *  past its step budget). `result` is then the last run that didn't. */
  error?: { index: number; message: string };
}

/**
 * Run `src`, then replay the stored reorderings on top of it, each against the
 * run the previous one produced:
 *
 *     run₀ = run(source, seed)          // the default order
 *     runₙ = apply(reorderₙ, runₙ₋₁)
 *
 * N reorderings cost N+1 runs. That is the whole re-execution model — there is
 * no incremental replay to get subtly wrong, and "we re-run from the beginning"
 * stays literally true.
 *
 * `opts.schedule` may already carry a forced prefix — a node chosen in the
 * timeline tree. A reordering's own override would replace it, so the two are
 * composed instead: the window is prepended with the picks the previous run
 * actually made before it. Re-forcing those changes nothing on its own (a run is
 * deterministic for its seed), which is exactly why it is safe to state them and
 * why it preserves the branch.
 */
export function runWithReorders(
  src: string,
  opts: RunOptions,
  reorders: readonly Reorder[]
): ReorderedRun {
  let result = run(src, { ...opts, causality: true });
  const statuses: ReorderStatus[] = [];

  for (const [index, r] of reorders.entries()) {
    const c = result.causality!;
    const moved = c.steps.findIndex((s) => s.id === r.moved);
    const target = c.steps.findIndex((s) => s.id === r.target);
    if (moved < 0 || target < 0) {
      statuses.push("stale");
      continue;
    }
    // Already where it was asked to be (an earlier reordering got it there):
    // nothing to force, and re-running would be a no-op anyway.
    const satisfied = r.side === "before" ? target === moved + 1 : target === moved - 1;
    if (satisfied) {
      statuses.push("applied");
      continue;
    }
    if (!canDrop(c, moved, target)) {
      statuses.push("stale");
      continue;
    }

    let next: RunResult;
    try {
      const window = toOverride(c, moved, target);
      const schedule = opts.schedule
        ? { from: 0, threads: [...c.steps.slice(0, window.from).map((s) => s.thread), ...window.threads] }
        : window;
      next = run(src, { ...opts, causality: true, schedule });
    } catch (e) {
      return {
        result,
        statuses: [...statuses, "unrealized"],
        error: { index, message: e instanceof Error ? e.message : String(e) },
      };
    }
    const nc = next.causality!;
    const m = nc.steps.findIndex((s) => s.id === r.moved);
    const t = nc.steps.findIndex((s) => s.id === r.target);
    const ok =
      !next.scheduleAbandoned &&
      m >= 0 &&
      t >= 0 &&
      (r.side === "before" ? m < t : m > t);
    statuses.push(ok ? "applied" : "unrealized");
    if (ok) result = next;
  }

  return { result, statuses };
}
